import { Zone } from "@fairfleet/geotab";
import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";
import useTheme from "@mui/material/styles/useTheme";
import Typography from "@mui/material/Typography";
import { LatLngBoundsExpression } from "leaflet";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CircleMarker, MapContainer, Polygon, Polyline, TileLayer, Tooltip as LeafletTooltip, useMap } from "react-leaflet";
import { StayRow } from "../models/StayRow";
import { LatLng, visitMapWindow, visitTrack } from "../utils/visitTrack";
import { MAP_TILE_LAYERS } from "@/constants/MapTiles";
import { useLogRecordsByRange } from "@/hooks/logRecord/useLogRecordsByRange";
import { colorToCss, FALLBACK_ZONE_COLOR } from "@/pages/zones/utils/zoneColor";
import "leaflet/dist/leaflet.css";

interface Props {
  /** The visit to show; null closes the drawer. */
  row: StayRow | null;
  zone?: Zone;
  onClose: () => void;
}

const DATE_TIME = new Intl.DateTimeFormat(undefined, { dateStyle: "short", timeStyle: "medium" });
const TILE_LAYER = MAP_TILE_LAYERS.find(layer => layer.id === "osm") ?? MAP_TILE_LAYERS[0];

/** The map sizes itself while the drawer slides in; fit again once the positions are there. */
function FitBounds({ bounds }: { bounds: LatLngBoundsExpression | null }) {
  const map = useMap();
  useEffect(() => {
    map.invalidateSize();
    if (bounds) map.fitBounds(bounds, { padding: [32, 32], maxZoom: 17 });
  }, [map, bounds]);
  return null;
}

/** Right-hand drawer with one visit: the zone, the track during the visit and the drive in and out. */
export default function ZoneVisitMapDrawer({ row, zone, onClose }: Props) {
  const { t } = useTranslation();
  const theme = useTheme();
  // Fixed per opened visit, so a running visit does not reload on every render.
  const range = useMemo(() => (row ? visitMapWindow(row.entry, row.exit, new Date()) : null), [row]);
  const { data: records = [], isLoading } = useLogRecordsByRange(
    row?.deviceId,
    range?.from ?? new Date(0),
    range?.to ?? new Date(0),
    !!row,
  );
  const track = useMemo(() => (row ? visitTrack(records, row.entry, row.exit) : null), [records, row]);
  const zonePositions = useMemo<LatLng[]>(
    () => ((zone?.points ?? []) as { x: number; y: number }[]).map(point => [point.y, point.x]),
    [zone],
  );
  const bounds = useMemo<LatLngBoundsExpression | null>(() => {
    // The zone and the visit fill the map; the drive in and out only reaches into it.
    const focus = [...zonePositions, ...(track?.inside ?? [])];
    const points = focus.length > 0 ? focus : [...(track?.before ?? []), ...(track?.after ?? [])];
    return points.length > 0 ? points : null;
  }, [zonePositions, track]);

  const zoneColor = colorToCss(zone?.fillColor ?? FALLBACK_ZONE_COLOR);
  const visitColor = theme.palette.primary.main;
  const passColor = theme.palette.text.secondary;
  const first = track?.inside[0];
  const last = track && row?.exit ? track.inside[track.inside.length - 1] : undefined;
  const hasPositions = !!track && track.before.length + track.inside.length + track.after.length > 0;

  return (
    <Drawer
      anchor="right"
      open={row !== null}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100%", md: "45%" }, display: "flex", flexDirection: "column" } }}
      aria-label={t("zoneReportPage.map.title")}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: "12px 16px", borderBottom: 1, borderColor: "divider" }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" noWrap>
            {[row?.zoneName || t("zoneReportPage.zoneUnknown"), row?.deviceName].filter(Boolean).join(" · ")}
          </Typography>
          {row && (
            <Typography variant="body2" color="text.secondary">
              {`${DATE_TIME.format(row.entry)} – ${row.exit ? DATE_TIME.format(row.exit) : t("zoneReportPage.ongoing")}`}
            </Typography>
          )}
        </Box>
        <IconButton onClick={onClose} aria-label={t("zoneReportPage.map.close")}>
          <CloseIcon />
        </IconButton>
      </Box>
      {isLoading && <LinearProgress aria-label={t("zoneReportPage.map.loading")} />}
      {row && (
        <Box sx={{ position: "relative", flex: 1, minHeight: 320, "& .leaflet-container": { height: "100%", width: "100%" } }}>
          <MapContainer center={[51.16, 10.45]} zoom={6} style={{ height: "100%", width: "100%" }}>
            <TileLayer url={TILE_LAYER.url} attribution={TILE_LAYER.attribution} maxZoom={TILE_LAYER.maxZoom} />
            {zonePositions.length > 0 && (
              <Polygon positions={zonePositions} pathOptions={{ color: zoneColor, fillColor: zoneColor, fillOpacity: 0.25, weight: 2 }}>
                <LeafletTooltip sticky>{zone?.name}</LeafletTooltip>
              </Polygon>
            )}
            {track && track.before.length > 1 && (
              <Polyline positions={track.before} pathOptions={{ color: passColor, weight: 3, opacity: 0.8, dashArray: "6 6" }} />
            )}
            {track && track.after.length > 1 && (
              <Polyline positions={track.after} pathOptions={{ color: passColor, weight: 3, opacity: 0.8, dashArray: "6 6" }} />
            )}
            {track && track.inside.length > 1 && (
              <Polyline positions={track.inside} pathOptions={{ color: visitColor, weight: 4, opacity: 0.9 }} />
            )}
            {first && (
              <CircleMarker center={first} radius={7} pathOptions={{ color: visitColor, fillColor: visitColor, fillOpacity: 1, weight: 2 }}>
                <LeafletTooltip>{t("zoneReportPage.map.entry", { time: DATE_TIME.format(row.entry) })}</LeafletTooltip>
              </CircleMarker>
            )}
            {last && row.exit && (
              <CircleMarker center={last} radius={7} pathOptions={{ color: visitColor, fillColor: "#fff", fillOpacity: 1, weight: 2 }}>
                <LeafletTooltip>{t("zoneReportPage.map.exit", { time: DATE_TIME.format(row.exit) })}</LeafletTooltip>
              </CircleMarker>
            )}
            <FitBounds bounds={bounds} />
          </MapContainer>
          <Box
            sx={{
              position: "absolute",
              left: 12,
              bottom: 24,
              zIndex: 1000,
              p: "6px 10px",
              bgcolor: "background.paper",
              borderRadius: 1,
              boxShadow: 1,
              typography: "caption",
            }}
          >
            {!isLoading && !hasPositions ? (
              t("zoneReportPage.map.noPositions")
            ) : (
              <>
                <Box component="span" sx={{ display: "inline-block", width: 18, borderTop: 3, borderColor: visitColor, mr: 0.75, verticalAlign: "middle" }} />
                {t("zoneReportPage.map.legendVisit")}
                <Box component="span" sx={{ display: "inline-block", width: 18, borderTop: "3px dashed", borderColor: passColor, mx: 0.75, verticalAlign: "middle" }} />
                {t("zoneReportPage.map.legendPass")}
              </>
            )}
          </Box>
        </Box>
      )}
    </Drawer>
  );
}
